// ===================================
// MAIN TOPICS PAGE - src/pages/TopicsPage.tsx
// ===================================
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { parseUserPermissions } from '../utils/permissions';
import { Topic } from '../types';
import TopicsTable from '../components/TopicsTable';
import TopicForm from '../components/TopicForm';
import TopicDetail from '../components/TopicDetail';
import { useNotifications } from '../contexts/NotificationContext';
import TopicsApi from '../services/topicsApi';

const TopicsPage: React.FC = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const permissions = parseUserPermissions(user);

  // Modal states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

  // Table refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle topic selection (view details)
  const handleTopicSelect = (topic: Topic) => {
    setSelectedTopic(topic);
    setShowDetail(true);
  };

  // Handle topic edit
  const handleTopicEdit = (topic: Topic) => {
    setSelectedTopic(topic);
    setShowEditForm(true);
  };

  // Handle topic creation
  const handleTopicCreate = () => {
    if (!permissions.canCreate) {
      addNotification({
        type: 'error',
        title: 'Permission Denied',
        message: 'You do not have permission to create topics'
      });
      return;
    }
    setShowCreateForm(true);
  };

  // Handle topic deletion
  // const handleTopicDelete = async (topic: Topic) => {
  //   if (!permissions.canDelete) {
  //     addNotification({
  //       type: 'error',
  //       title: 'Permission Denied',
  //       message: 'You do not have permission to delete topics'
  //     });
  //     return;
  //   }

  //   // Prevent deletion if topic has actions
  //   if (topic.action_count > 0) {
  //     addNotification({
  //       type: 'error',
  //       title: 'Cannot delete topic',
  //       message: `Topic "${topic.name}" has ${topic.action_count} action(s). Please delete all actions first.`
  //     });
  //     return;
  //   }

  //   // Confirm deletion
  //   if (!window.confirm(`Are you sure you want to delete the topic "${topic.name}"?`)) {
  //     return;
  //   }

  //   try {
  //     console.log('Deleting topic:', topic.name, 'ID:', topic.id);
      
  //     await TopicsApi.deleteTopic(topic.id);
      
  //     addNotification({
  //       type: 'success',
  //       title: 'Topic deleted',
  //       message: `Topic "${topic.name}" has been deleted successfully`
  //     });

  //     // Close detail modal if it's open
  //     if (showDetail && selectedTopic?.id === topic.id) {
  //       setShowDetail(false);
  //       setSelectedTopic(null);
  //     }

  //     // Refresh table
  //     setRefreshTrigger(prev => prev + 1);

  //   } catch (err) {
  //     const error = TopicsApi.handleApiError(err);
  //     addNotification({
  //       type: 'error',
  //       title: 'Failed to delete topic',
  //       message: error.message
  //     });
  //   }
  // };

  // Handle topic deletion
const handleTopicDelete = async (topic: Topic) => {
  if (!permissions.canDelete) {
    addNotification({
      type: 'error',
      title: 'Permission Denied',
      message: 'You do not have permission to delete topics'
    });
    return;
  }

  // Prevent deletion if topic has actions
  if (topic.action_count > 0) {
    addNotification({
      type: 'error',
      title: 'Cannot delete topic',
      message: `Topic "${topic.name}" has ${topic.action_count} action${topic.action_count !== 1 ? 's' : ''}. Please delete all actions before deleting the topic.`
    });
    return;
  }

  // Confirm deletion
  if (!window.confirm(`Are you sure you want to delete the topic "${topic.name}"? This action cannot be undone.`)) {
    return;
  }

  try {
    console.log('Deleting topic:', topic.name, 'ID:', topic.id);
    
    await TopicsApi.deleteTopic(topic.id);
    
    addNotification({
      type: 'success',
      title: 'Topic deleted',
      message: `Topic "${topic.name}" has been deleted successfully`
    });

    // Close detail modal if it's open
    if (showDetail && selectedTopic?.id === topic.id) {
      setShowDetail(false);
      setSelectedTopic(null);
    }

    // Refresh table
    setRefreshTrigger(prev => prev + 1);

  } catch (err) {
    const error = TopicsApi.handleApiError(err);
    
    // Check if error is about having actions
    if (error.message.includes('action')) {
      addNotification({
        type: 'error',
        title: 'Cannot delete topic',
        message: error.message
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Failed to delete topic',
        message: error.message
      });
    }
  }
};

  // Handle form success (create/edit)
  const handleFormSuccess = (topic: Topic) => {
    // Update selected topic if we're editing
    if (showEditForm && selectedTopic?.id === topic.id) {
      setSelectedTopic(topic);
    }

    // Refresh table
    setRefreshTrigger(prev => prev + 1);
  };

  // Close modals
  const closeModals = () => {
    setShowCreateForm(false);
    setShowEditForm(false);
    setShowDetail(false);
    setSelectedTopic(null);
  };

  // Handle edit from detail view
  const handleEditFromDetail = (topic: Topic) => {
    setShowDetail(false);
    setSelectedTopic(topic);
    setShowEditForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Main Table */}
      <TopicsTable
        key={refreshTrigger} // Force refresh when this changes
        onTopicSelect={handleTopicSelect}
        onTopicEdit={handleTopicEdit}
        onTopicCreate={handleTopicCreate}
        onTopicDelete={handleTopicDelete}
      />

      {/* Create Topic Form */}
      <TopicForm
        isOpen={showCreateForm}
        mode="create"
        onClose={() => setShowCreateForm(false)}
        onSuccess={handleFormSuccess}
      />

      {/* Edit Topic Form */}
      <TopicForm
        isOpen={showEditForm}
        mode="edit"
        topic={selectedTopic}
        onClose={() => setShowEditForm(false)}
        onSuccess={handleFormSuccess}
      />

      {/* Topic Detail View */}
      <TopicDetail
        isOpen={showDetail}
        topic={selectedTopic}
        onClose={() => setShowDetail(false)}
        onEdit={handleEditFromDetail}
        onDelete={handleTopicDelete}
      />
    </div>
  );
};

export default TopicsPage;